import { play } from 'cuelume'

import { dedupeTargets } from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import {
  buildScaleAlgoParams,
  planScaleRun,
  scaleLadderErrorMessage,
} from '@/features/scale/lib/build-scale-params'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import type { useStartScaleAlgo } from '@/service/algo.hook'
import type { Instrument } from '@/types/generated/common/types'

export type SubmitScaleDeps = {
  instrumentMap: Map<string, Instrument>
  startScale: ReturnType<typeof useStartScaleAlgo>['mutateAsync']
}

// Submit a Scale ladder. Single instrument, single account — the wire params carry one
// run, so more than one resolved row is refused here (order-form-summary.tsx already
// disables submit in that case; this is the defensive second check). The run is planned
// by planScaleRun, the same call behind the preview and the submit gate, so a refusal
// surfaces before the round trip; the server re-plans at start and stays authoritative.
export async function submitScaleOrder(ctx: SubmitterContext, deps: SubmitScaleDeps) {
  const { t, form } = ctx
  const { instrumentMap, startScale } = deps
  if (!ctx.validateSubmissionRows()) return
  const rows = ctx.getSubmissionRows()

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }
  if (rows.length > 1) {
    toast.error(t('scaleForm.singleAccountOnly'), { position: 'bottom-right' })
    return
  }

  ctx.setIsSubmitting(true)

  try {
    const { item, rawItem, account } = rows[0]
    const amount = rawItem.amount
    if (!amount || amount <= 0) {
      throw new Error(t('enterValidAmount'))
    }

    const values = form.getValues()
    const run = planScaleRun(instrumentMap, item, amount, values)
    if (run.plan && !run.plan.ok) throw new Error(scaleLadderErrorMessage(run.plan.error, t))

    const scaleParams = buildScaleAlgoParams({
      symbol: item.symbol,
      side: item.side,
      accountId: account.accountId,
      run,
      reduceOnly: values.reduceOnly,
      postOnly: values.postOnly,
    })

    const scaleSubmissionId = randomUUID()
    await startScale({ ...scaleParams, correlationId: scaleSubmissionId })

    trackOrderItems('scale', [scaleParams], ctx.getAnalyticsAccounts(), 'order-form')
    const selection = ctx.getSelection()
    ctx.triggerOptimistic(
      scaleSubmissionId,
      dedupeTargets([scaleParams]),
      selection.instruments,
      selection.options
    )
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('scaleForm.submissionFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}
